import React from 'react';
import { PageSection, StatsSectionContent } from '../../types/page';
import { getIcon } from '../../lib/iconMapper';

interface StatsSectionProps {
  section: PageSection;
  className?: string;
}

export const StatsSection: React.FC<StatsSectionProps> = ({ section, className = '' }) => {
  const content = section.content as StatsSectionContent;
  
  if (!content.items || content.items.length === 0) {
    return null;
  }

  const backgroundStyle = content.backgroundImage 
    ? { backgroundImage: `url(${content.backgroundImage})` }
    : {};

  return (
    <section 
      className={`relative py-20 bg-cover bg-center bg-no-repeat ${className}`}
      style={backgroundStyle}
    >
      {/* Overlay */}
      {content.backgroundImage && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      
      <div className="relative z-10 container mx-auto px-4">
        {/* Section Header */}
        {(section.title || content.title) && (
          <div className="text-center mb-16">
            <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${
              content.backgroundImage ? 'text-white' : 'text-gray-900'
            }`}>
              {section.title || content.title}
            </h2>
            {section.subtitle && (
              <p className={`text-xl max-w-2xl mx-auto ${
                content.backgroundImage ? 'text-gray-200' : 'text-gray-600'
              }`}>
                {section.subtitle}
              </p>
            )}
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {content.items.map((stat, index) => {
            const IconComponent = getIcon(stat.icon);
            
            return (
              <div 
                key={index}
                className={`text-center p-6 rounded-lg transition-transform hover:scale-105 ${
                  content.backgroundImage 
                    ? 'bg-white/10 backdrop-blur-sm border border-white/20' 
                    : 'bg-white shadow-lg border border-gray-100'
                }`}
              >
                {IconComponent && (
                  <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    content.backgroundImage
                      ? 'bg-white/20 text-white'
                      : 'bg-blue-100 text-blue-600'
                  }`}>
                    <IconComponent className="w-8 h-8" />
                  </div>
                )}
                
                <div className={`text-4xl font-bold mb-2 ${
                  content.backgroundImage ? 'text-white' : 'text-gray-900'
                }`}>
                  {stat.value}
                </div>
                
                <div className={`text-lg font-medium mb-1 ${
                  content.backgroundImage ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  {stat.label}
                </div>
                
                {stat.description && (
                  <div className={`text-sm ${
                    content.backgroundImage ? 'text-gray-300' : 'text-gray-500'
                  }`}>
                    {stat.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};